import { logger } from '@fe/core';
import { FeMapPlugin, ThemeChangedListener } from '@fe/maplibre-extensions';
import { SlotLayer } from '@fe/weather';
import {
    AddLayerObject,
    Map as MaplibreMap,
    MapOptions,
    Marker,
    PaddingOptions,
    setWorkerUrl,
    Source,
    StyleLayer,
    StyleSpecification,
    Subscription
} from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import Coordinate from '../../../common/coordinate';
import { MapLanguageMode } from '../../../common/enums';
import { isLight } from '../../../common/helpers';
import { TERRAIN_SPECIFICATION } from '../../../components/map-interaction/TerrainControl';
import { MapSourceUrlReplacementInfo } from '../../../types/other';
import { UDViewMapModel, UDViewMapStyle } from '../../../udView/udview';
import { store } from '../../state/store';
import { getMapStyle } from './map-styles';
import { applyLanguageToStyle, buildNameExpression, tokenReplaceStyle } from './style-token-replace';

setWorkerUrl(workerUrl);

export class FeMap extends MaplibreMap {
    isLightStyle = false;
    private themeChangedListeners: ThemeChangedListener[] = [];
    private removed = false;
    private styleLoaded = false;
    private currentUdViewStyle = UDViewMapStyle.light;
    private currentStyle: StyleSpecification | null = null;
    private plugins: FeMapPlugin[] = [];
    private mapTokens: MapSourceUrlReplacementInfo | null = null;
    private currentLanguage: MapLanguageMode = MapLanguageMode.english;
    private deviceLocale: string | undefined;
    private nameLayerIds: string[] = [];
    private resizeTimeout: number | null = null;
    private constraintToSingleWorld = true;

    constructor(options: MapOptions & { constraintToSingleWorld?: boolean }) {
        super(options);
        this.constraintToSingleWorld = options.constraintToSingleWorld ?? false;
        this.on('style.load', () => {
            // TODO: move those into style JSON files
            this.setGlobalStateProperty('route-text-color', this.isLightStyle ? '#383838' : '#fdfdfd');

            // Starting with maplibre 5.16.0, this event will be called every time the map style is loaded/reload even with diff: true
            if (this.styleLoaded) {
                return;
            }

            this.styleLoaded = true;
            try {
                this.addSlots();
                this.plugins.forEach((x) => x.onAdd?.(this));
            } catch (err) {
                logger.trackException(err);
            }
        });

        if (this.constraintToSingleWorld) {
            this.setZoomForSingleWorld();
            window.addEventListener('resize', this.windowResizeEventHandler);
        }
    }

    onThemeChanged(listener: ThemeChangedListener): Subscription {
        this.themeChangedListeners.push(listener);
        return {
            unsubscribe: () => {
                const idx = this.themeChangedListeners.indexOf(listener);
                if (idx !== -1) {
                    this.themeChangedListeners.splice(idx, 1);
                }
            }
        };
    }

    addPlugin(plugin: FeMapPlugin): this {
        if (this.plugins.includes(plugin)) {
            return this;
        }

        this.plugins.push(plugin);
        if (this.styleLoaded) {
            plugin.onAdd?.(this);
        }

        return this;
    }

    removePlugin(plugin: FeMapPlugin): this {
        const idx = this.plugins.indexOf(plugin);
        if (idx > -1) {
            this.plugins.splice(idx, 1);
            if (this.styleLoaded) {
                plugin.onRemove?.(this);
            }
        }
        return this;
    }

    remove(): void {
        window.removeEventListener('resize', this.windowResizeEventHandler);
        this.plugins.forEach((x) => x.onRemove?.(this));
        this.plugins = [];
        super.remove();
        this.removed = true;
    }

    addLayer(layer: AddLayerObject, beforeId?: string): this {
        super.addLayer(layer, beforeId);
        return this;
    }

    removeLayer(id: string): this {
        if (this.removed) {
            return this;
        }

        return super.removeLayer(id);
    }

    removeSource(id: string): this {
        if (this.removed) {
            return this;
        }
        return super.removeSource(id);
    }

    getLayer(id: string): StyleLayer | undefined {
        if (this.removed) {
            return undefined;
        }
        return super.getLayer(id);
    }

    getSource<TSource extends Source>(id: string): TSource | undefined {
        if (this.removed) {
            return undefined;
        }
        return super.getSource(id);
    }

    // Goes to the specified coordinates with a flashing marker, if zoom is lower than specified zoom it will zoom in
    goToCoordinates({ lat, lng }: Coordinate, zoom: number = 6) {
        this.easeTo({
            center: [lng, lat],
            zoom: Math.max(zoom, this.getZoom())
        });

        this.createFlashingMarker(lng, lat);
    }

    goToAirport(position: Coordinate) {
        if (!position.isValid) {
            return;
        }

        this.easeTo({
            center: [position.lng, position.lat],
            zoom: 10
        });
    }

    // Goes to flight position if the flight is out of view, does nothing if the flight is already in view
    goToFlight(coordinate: Coordinate, zoom: number, padding?: PaddingOptions) {
        if (!coordinate.isValid) {
            return;
        }
        this.easeTo({
            center: [coordinate.lng, coordinate.lat],
            padding: padding ?? 0, // Don't pass undefined because it causes a crash
            zoom
        });

        this.createFlashingMarker(coordinate.lng, coordinate.lat);
    }

    encompass(bbox: [number, number, number, number], padding: PaddingOptions) {
        this.fitBounds(bbox, { padding });
    }

    setMapTokens(tokens: MapSourceUrlReplacementInfo) {
        this.mapTokens = tokens;
        if (this.styleLoaded && this.currentStyle) {
            const styleJson = this.prepareStyle(this.currentUdViewStyle, this.mapTokens);
            this.setStyleInternal(styleJson);
        }
    }

    setMapLanguage(language: MapLanguageMode, locale?: string) {
        this.currentLanguage = language;
        this.deviceLocale = locale;

        if (!this.styleLoaded) {
            return;
        }

        const expression = buildNameExpression(language, locale);
        for (const layerId of this.nameLayerIds) {
            this.setLayoutProperty(layerId, 'text-field', expression);
        }
    }

    loadUdViewStyle(style: UDViewMapStyle): boolean {
        if (!this.mapTokens) {
            return false;
        }
        const styleJson = this.prepareStyle(style, this.mapTokens);
        const oldIsLight = this.isLightStyle;
        this.isLightStyle = isLight(style);
        this.currentUdViewStyle = style;
        this.setStyleInternal(styleJson);

        if (this.isLightStyle !== oldIsLight) {
            this.themeChangedListeners.forEach((x) => x({ isLight: this.isLightStyle }));
        }
        return true;
    }

    loadUdView(view: UDViewMapModel): boolean {
        if (!this.mapTokens) {
            return false;
        }

        // TODO: handle multiple calls correctly
        const style = this.prepareStyle(view.style, this.mapTokens);
        this.isLightStyle = isLight(view.style);
        this.currentUdViewStyle = view.style;
        this.setStyleInternal(style); // <- Synchronous

        // At this point it is save to add/remove layers and source because the setStyle is synchronous when given an object instead of a url
        this.jumpTo(
            {
                center: [view.lon, view.lat],
                zoom: view.zoom,
                bearing: view.useGlobeView ? 0 : view.bearing,
                pitch: view.useGlobeView ? 0 : view.pitch
            },
            { initialUdViewLoad: true }
        );

        // Somehow can't set this unless style is loaded
        if (this.styleLoaded) {
            this.setProjection({
                type: view.useGlobeView ? 'globe' : 'mercator'
            });
            this.setTerrain(view.terrain ? TERRAIN_SPECIFICATION : null);
        }

        return true;
    }

    private prepareStyle(style: UDViewMapStyle, replacement: MapSourceUrlReplacementInfo): StyleSpecification {
        const styleSpec = getMapStyle(style);
        tokenReplaceStyle(styleSpec, replacement);
        this.nameLayerIds = applyLanguageToStyle(styleSpec, this.currentLanguage, this.deviceLocale);
        return styleSpec;
    }

    private setStyleInternal(newStyle: StyleSpecification) {
        let previousOrder: string[] | undefined = undefined;
        if (this.styleLoaded) {
            // Hold on to the previous layer order. We'll need it to reposition any custom layers
            previousOrder = this.getLayersOrder();

            // When terrain is active, maplibre's style diff algorithm crashes internally
            // ("Unable to perform style diff: Cannot read properties of undefined (reading '_checkLoaded')"),
            // falling back to a full rebuild from scratch. This loses all custom layers/sources
            // added by plugins via onAdd. By removing terrain before the setStyle call, the diff
            // succeeds and transformStyle can properly preserve custom fe-* layers and sources.
            if (this.getTerrain() != null) {
                this.setTerrain(null);
            }
        }

        //TODO: fallback fonts require logic on the server side to work properly. Disable for now until we know how to proceed.

        // Add a fallback font to the style to avoid missing glyphs for non-latin characters.
        // const fallbackFont = 'Noto Sans';
        // newStyle.layers.forEach((layer) => {
        //     if (layer.layout && layer.type === 'symbol' && layer.layout['text-font']) {
        //         const fonts = layer.layout['text-font'] as string[];
        //         if (!fonts.includes(fallbackFont)) {
        //             layer.layout['text-font'] = [...fonts, fallbackFont];
        //         }
        //     }
        // });

        this.setStyle(newStyle, {
            diff: true,
            transformStyle: (previousStyle, nextStyle) => {
                if (!previousStyle) {
                    return nextStyle;
                }
                // Custom layers will not appear in previousStyle because they can't be serialized into style spec JSON
                // They will be preserved, but may appear in the wrong order
                const customLayers = previousStyle.layers.filter((x) => x.id.startsWith('fe-'));
                return {
                    ...nextStyle,
                    sources: {
                        ...nextStyle.sources,
                        // Copy our sources
                        ...Object.fromEntries(Object.entries(previousStyle.sources).filter(([k]) => k.startsWith('fe-')))
                    },
                    layers: [
                        ...nextStyle.layers.map((x) => {
                            const prev = previousStyle.layers.find((l) => l.id === x.id);
                            if (prev) {
                                // Carry over layout and filter properties of non-base style layers
                                // At runtime we don't modify base style layers so we don't need to copy anything from the previous layer stack and doing so would 
                                // cause issues if the styles actually differ.
                                if ('source' in prev && prev.source !== 'febase') {
                                    x.layout = { ...x.layout, ...prev.layout };
                                    // Carry over runtime-set filter (e.g. from map.setFilter()).
                                    if ('filter' in prev && prev.filter !== undefined) {
                                        (x as any).filter = prev.filter;
                                    }
                                }
                            }
                            return x;
                        }),
                        ...customLayers
                    ],
                    // Here we could also set center, zoom, pitch and bearing, but maplibre code that diffs the styles doesn't include changes in those values in the change list
                    // therefore setting those here would have no effect
                    projection: {
                        type: previousStyle?.projection?.type ?? 'mercator'
                    },
                    // Terrain bug workaround
                    terrain: undefined
                };
            }
        });

        if (this.styleLoaded && previousOrder && this.currentStyle) {
            /* When transform style above is called, the previous and next style that are passed in as serialized to maplibre style spec.
             * In maplibre style there's no custom layer defined. Custom layers only exist in maplibre implementation. As a consequence
             * when comparing previous style with next we're not getting the custom layers carried over in the right order.
             * All the custom layers are preserved but they will be pushed to the back or front of the layer stack. That breaks our layer ordering.
             *
             * Example:
             * Before style diff: [base-map-layers, **custom-layer**, nav-layer, other-non-style-custom-layers]
             * After style diff.  [base-map-layers, nav-layer, **custom-layer**, other-non-style-custom-layers]
             *
             * Here we make the following assumptions:
             *  - nav data source is named festatic
             *  - base map layers are contiguous (they do not interleave with other map layers)
             *  - nav layers are the same between different map styles
             *  - the order is: base-map, non style layers mixed with nav layers
             *
             * The solution currently is to
             *  1. take the previous order, slice the part that of the stack that is above the last base-map layers
             *     (those would be all non-style layers and nav layers mixed)
             *  2. take the new style and slice the part from first to last base map layer
             *  3. combine the order [...2, ...1]. This should give us the new style's base map layers first and then
             *     the correct order of nav layers mixed with custom and non-style layers. This only work under the assumptions mentioned above.
             *
             * FIXME: the most robust solution would be to implement mapbox slot mechanism. They define a new serializable layer type called 'slot'.
             * Then when diffing style the slots are preserved. When adding a new layer you can specify a slot which is appended to the layer id in a form
             * of a fully-qualified-id. Then we can easily rebuild the layer order after diffing the style without making any of the assumptions above.
             */

            // To find the last base map layer find find first nav layer and move 1 back

            const lastBaseLayerNewStyle = newStyle.layers.findIndex((x) => 'source' in x && x.source === 'festatic') - 1;
            const newBaseMapLayerIds = newStyle.layers.slice(0, lastBaseLayerNewStyle + 1).map((x) => x.id);

            const lastBaseLayerOldStyle =
                this.currentStyle.layers[this.currentStyle.layers.findIndex((x) => 'source' in x && x.source === 'festatic') - 1].id;

            const oldLayerOrderWithoutBaseStyle = previousOrder.slice(previousOrder.indexOf(lastBaseLayerOldStyle) + 1);

            // Rebuild the order
            this.style._order = [
                // only take base map layers from the new style
                ...newBaseMapLayerIds,
                // carry over nav and non-style layers from previous style (assumes nav layers are the same between map style, currently it's true)
                ...oldLayerOrderWithoutBaseStyle
            ];

            this.style._layerOrderChanged = true;
        }

        this.currentStyle = newStyle;
    }

    private addSlots() {
        // Check weather.md for ordering
        const firstNavLayer = this.style.stylesheet.layers.find((x) => 'source' in x && x.source === 'festatic')?.id;

        this.addSlotLayer('fe-terrain-shade-slot', firstNavLayer);
        this.addSlotLayer('fe-day-night-slot', firstNavLayer);
        this.addSlotLayer('fe-wms-slot', firstNavLayer);
        this.addSlotLayer('fe-weather-slot', firstNavLayer);

        this.addSlotLayer('fe-navaid-rings', 'navaid');
        this.addSlotLayer('fe-airport-monitor-status-slot', 'airport');
        this.addSlotLayer('fe-airport-range-rings-slot', 'airport');

        // everything else is on top of the nav layers and all base map layers
        this.addSlotLayer('fe-drawing-objects-slot');
        this.addSlotLayer('fe-user-geojson-slot');
        this.addSlotLayer('fe-diversion-icons');
        this.addSlotLayer('fe-brightness-slot');
        this.addSlotLayer('fe-routes-slot');
        this.addSlotLayer('fe-route-analyzer-slot');
        this.addSlotLayer('fe-geo-grid-slot');
        this.addSlotLayer('fe-stars-slot');
        this.addSlotLayer('fe-atmosphere-slot');
        this.addSlotLayer('fe-deck-slot');
    }

    private addSlotLayer(id: string, beforeId?: string) {
        this.addLayer(new SlotLayer(id), beforeId);
        this.setLayoutProperty(id, 'visibility', 'none');
    }

    private createFlashingMarker(lng: number, lat: number, durationSeconds?: number) {
        // Remove any existing flash markers
        const existingMarker = document.getElementById('flash-marker-container');
        if (existingMarker) {
            existingMarker.remove();
        }

        const totalDurationMs = Math.max(1, durationSeconds ?? store.getState().settings.mapFlashMarkerDurationSeconds) * 1000;
        const burstDurationMs = 1400; // Initial fast ripples to grab attention, regardless of total duration
        const fadeOutMs = 700; // How long the final fade-out takes
        // Once past the burst, switch to a slow idle pulse until it's time to fade out
        const fadeStartMs = Math.max(burstDurationMs, totalDurationMs - fadeOutMs);

        // Create a marker container
        const markerContainer = document.createElement('div');
        markerContainer.id = 'flash-marker-container';

        // MapLibre's Marker manages `style.opacity` on the root element itself (for terrain-occlusion
        // fading) and overwrites it on every render frame, which fights any CSS-class-based opacity
        // transition set directly on that element. Do the fade on this inner wrapper instead.
        const fadeWrapper = document.createElement('div');
        fadeWrapper.className = 'flash-marker-fade-wrapper';
        markerContainer.appendChild(fadeWrapper);

        // Create the fast attention-grabbing burst ripples
        for (let i = 0; i < 3; i++) {
            const burstRing = document.createElement('div');
            burstRing.className = 'burst-ring';
            burstRing.style.animationDelay = `${i * 0.4}s`;
            fadeWrapper.appendChild(burstRing);
        }

        // Create the slow, looping "beacon" ripple used for the remainder of the display time
        const pulseRing = document.createElement('div');
        pulseRing.className = 'pulse-ring';
        pulseRing.style.animationDelay = `${burstDurationMs}ms`;
        fadeWrapper.appendChild(pulseRing);

        // Create the center dot
        const centerDot = document.createElement('div');
        centerDot.className = 'center-dot';
        fadeWrapper.appendChild(centerDot);

        if (!document.getElementById('flash-marker-style')) {
            const style = document.createElement('style');
            style.id = 'flash-marker-style';
            style.textContent = `
            #flash-marker-container {
                position: relative;
                width: 50px;
                height: 50px;
                transform: translate(-25px, -25px);
                pointer-events: none;
                filter: drop-shadow(0 0 1.5px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 3px rgba(0, 0, 0, 0.15));
            }

            .flash-marker-fade-wrapper {
                position: absolute;
                inset: 0;
                opacity: 1;
                transition: opacity ${fadeOutMs}ms ease;
            }

            .flash-marker-fade-wrapper.fading {
                opacity: 0;
            }

            .center-dot {
                position: absolute;
                top: 50%;
                left: 50%;
                width: 9px;
                height: 9px;
                background-color: rgba(255, 215, 0, 1);
                border-radius: 50%;
                transform: translate(-50%, -50%);
                z-index: 2;
                box-shadow: 0 0 5px 1px rgba(255, 215, 0, 0.85);
                animation: dot-pulse 2.4s ease-in-out infinite;
            }

            .burst-ring,
            .pulse-ring {
                position: absolute;
                top: 50%;
                left: 50%;
                width: 10px;
                height: 10px;
                background-color: transparent;
                border: 2px solid rgba(255, 215, 0, 0.8);
                border-radius: 50%;
                transform: translate(-50%, -50%) scale(0);
                opacity: 0;
            }

            .burst-ring {
                animation: ripple-burst 1.2s cubic-bezier(0.2, 0.7, 0.4, 1) forwards;
            }

            .pulse-ring {
                border-color: rgba(255, 215, 0, 0.6);
                animation: ripple-pulse 2.4s cubic-bezier(0.2, 0.7, 0.4, 1) infinite;
            }

            @keyframes ripple-burst {
                0% {
                    transform: translate(-50%, -50%) scale(0);
                    opacity: 1;
                }
                50% {
                    opacity: 0.5;
                }
                100% {
                    transform: translate(-50%, -50%) scale(5);
                    opacity: 0;
                }
            }

            @keyframes ripple-pulse {
                0% {
                    transform: translate(-50%, -50%) scale(0.3);
                    opacity: 0.7;
                }
                70% {
                    opacity: 0.15;
                }
                100% {
                    transform: translate(-50%, -50%) scale(4);
                    opacity: 0;
                }
            }

            @keyframes dot-pulse {
                0%, 100% {
                    box-shadow: 0 0 4px 1px rgba(255, 215, 0, 0.75);
                    transform: translate(-50%, -50%) scale(1);
                }
                50% {
                    box-shadow: 0 0 10px 4px rgba(255, 215, 0, 1);
                    transform: translate(-50%, -50%) scale(1.25);
                }
            }
        `;
            document.head.appendChild(style);
        }

        // Add marker to map
        const marker = new Marker({
            element: markerContainer,
            anchor: 'center'
        })
            .setLngLat([lng, lat])
            .addTo(this);

        // Start fading out shortly before the marker is removed
        self.setTimeout(() => {
            fadeWrapper.classList.add('fading');
        }, fadeStartMs);

        // Remove the marker once the fade-out animation completes
        self.setTimeout(
            () => {
                marker.remove();
            },
            fadeStartMs + fadeOutMs + 50
        );
    }

    private setZoomForSingleWorld() {
        const camera = this.cameraForBounds(
            [
                [-180, 0],
                [180, 0]
            ],
            { maxZoom: 22 }
        );
        if (camera) {
            this.setMinZoom(camera.zoom);
        }
    }

    private windowResizeEventHandler = () => {
        if (this.resizeTimeout) {
            self.clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = self.setTimeout(() => {
            this.resizeTimeout = null;
            this.setZoomForSingleWorld();
        }, 200);
    };
}
